<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { downloads } from '@integro/core';
import type { SpinnerType } from 'bootstrap-vue-next';
import ItpIcon from '../icon/ItpIcon.vue';

const props = withDefaults(
  defineProps<{
    text?: string;
    icon?: string;
    href?: string;
    tooltip?: string;
    target?: string;
    external?: boolean;
    useAuth?: boolean;
    noDownloadLink?: boolean;
    forceDownload?: boolean;
    noWrap?: boolean;
    button?: boolean;
    variant?: string;
    to?: string | Record<string, any>;
    disabled?: boolean;
    downloadFileName?: string;
    showSpinner?: boolean;
    spinnerType?: SpinnerType;
  }>(),
  {
    target: '_self',
    external: false,
    useAuth: false,
    noDownloadLink: false,
    forceDownload: false,
    noWrap: false,
    button: false,
    disabled: false,
    showSpinner: false,
    spinnerType: 'grow',
  },
);

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const isMouseOver = ref(false);
const spinning = ref(false);

const url = computed(() =>
  props.href ? downloads.normalizeLink(props.href) : '',
);

const className = computed(() => {
  const result: Record<string, boolean> = {
    'itp-link': true,
    'btn btn-sm': props.button,
  };

  if (props.button && props.variant) {
    result['border'] = props.variant === 'light';
    result[`btn-${props.variant}`] = true;
  }

  return result;
});

const isDisabled = computed(() => props.disabled);

function hasValidRouteParams(to: string | Record<string, any>): boolean {
  if (typeof to === 'object' && to.params) {
    const params = to.params as Record<string, any>;
    for (const key in params) {
      if (params[key] == null) {
        return false;
      }
    }
  }
  return true;
}

const renderRouterLink = computed(() => {
  if (!props.to) {
    return false;
  }
  return hasValidRouteParams(props.to);
});

const safeTo = computed(() => {
  if (!props.to || !hasValidRouteParams(props.to)) {
    return '/';
  }
  return props.to;
});

const renderTextOrIcon = computed(
  () => !renderRouterLink.value && (props.text || props.icon),
);

const renderDownload = computed(
  () => !renderRouterLink.value && props.useAuth && !props.noDownloadLink,
);

function onClick(ev: MouseEvent) {
  if (props.forceDownload) {
    onDownloadClick(ev);
    return;
  }

  if (props.useAuth) {
    ev.preventDefault();

    if (props.showSpinner) {
      spinning.value = true;
    }

    Promise.resolve(downloads.getFileBLOB(url.value!, props.downloadFileName))
      .then((p) => p && downloads.showFileInNewTab(p.file, p.fileName))
      .finally(() => (spinning.value = false));
    return;
  }

  if (!props.href) {
    ev.preventDefault();
    emit('click', ev);
  }
}

function onDownloadClick(ev: MouseEvent) {
  if (props.useAuth) {
    ev.preventDefault();

    Promise.resolve(
      downloads.getFileBLOB(url.value!, props.downloadFileName),
    ).then((p) => p && downloads.downloadFile(p.file, p.fileName));
  }
}
</script>

<template>
  <div
    :class="className"
    :title="tooltip"
    @mouseover="isMouseOver = true"
    @mouseleave="isMouseOver = false"
  >
    <BSpinner v-if="spinning" small class="me-1" :type="spinnerType" />

    <RouterLink
      v-if="renderRouterLink"
      :to="safeTo"
      :class="{ 'text-nowrap': noWrap }"
      @click="emit('click', $event)"
    >
      <ItpIcon v-if="icon" :name="icon" class="me-1" />
      <slot>{{ text }}</slot>
    </RouterLink>

    <a
      v-if="renderTextOrIcon"
      :href="url"
      :target="target"
      :class="{ 'text-nowrap': noWrap, disabled: isDisabled }"
      @click="onClick"
    >
      <ItpIcon v-if="icon" :name="icon" class="me-1" />
      <slot>{{ text }}</slot>
      <ItpIcon
        v-if="external"
        class="text-muted"
        :font-scale="0.75"
        name="fa-external-link-alt"
      />
    </a>

    <a
      v-if="renderDownload"
      href="#"
      :class="{ disabled: isDisabled }"
      @click="onDownloadClick"
    >
      <ItpIcon name="fa-download" class="ms-1" />
    </a>

    <slot v-if="isMouseOver" name="mouseOver" />
  </div>
</template>

<style scoped>
.itp-link {
  display: inline-block;
}

.disabled {
  pointer-events: none;
  opacity: 0.65;
}
</style>
